/**
 * `labor.device_session.page` wire values for Kitting (queue vs multi-job pick workspace).
 * Stored in Postgres as station `kitting` plus optional `job_key` (sorted cart slot ids).
 */

/** Queue route — not a clocked-in work surface. */
export const KITTING_LIST_LABOR_PAGE = "kitting";

const KITTING_PICK_JOB_PREFIX = "kitting-pick:";
const KITTING_PICK_RUN_JOB_PREFIX = "kitting-pick-run:";

/** Stable labor page key for a durable kitting pick run. */
export function kittingPickLaborPageKeyForRun(runId: number): string {
  return `${KITTING_PICK_RUN_JOB_PREFIX}${String(runId)}`;
}

export function kittingPickRunIdFromLaborPageKey(pageKey: string): number | null {
  const trimmed = pageKey.trim();
  if (!trimmed.startsWith(KITTING_PICK_RUN_JOB_PREFIX)) return null;
  const runId = Number.parseInt(trimmed.slice(KITTING_PICK_RUN_JOB_PREFIX.length), 10);
  return Number.isFinite(runId) && runId > 0 ? runId : null;
}

/** Stable labor page key for a kitting pick session over the selected cart slots. */
export function kittingPickLaborPageKeyForSlots(slotIds: readonly string[]): string {
  const normalized = [...slotIds]
    .map((slotId) => slotId.trim())
    .filter((slotId) => slotId.length > 0)
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
  if (normalized.length === 0) return "";
  return `${KITTING_PICK_JOB_PREFIX}${normalized.join(",")}`;
}

export function kittingPickLaborExpectedPageFromPathname(pathname: string, search = ""): string {
  if (!pathname.startsWith("/manufacturing/kitting/pick")) {
    if (pathname.startsWith("/manufacturing/kitting")) {
      return KITTING_LIST_LABOR_PAGE;
    }
    return KITTING_LIST_LABOR_PAGE;
  }
  const params = new URLSearchParams(search);
  const runRaw = params.get("run");
  if (runRaw != null && runRaw.trim() !== "") {
    const runId = Number.parseInt(runRaw.trim(), 10);
    if (Number.isFinite(runId) && runId > 0) {
      return kittingPickLaborPageKeyForRun(runId);
    }
  }
  const slotsRaw = params.get("slots");
  if (slotsRaw == null || slotsRaw.trim() === "") {
    return KITTING_LIST_LABOR_PAGE;
  }
  const slotIds = slotsRaw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const pageKey = kittingPickLaborPageKeyForSlots(slotIds);
  return pageKey !== "" ? pageKey : KITTING_LIST_LABOR_PAGE;
}

/** Parses slot ids from a `kitting-pick:` wire page key. */
export function kittingPickSlotIdsFromLaborPageKey(pageKey: string): string[] {
  const trimmed = pageKey.trim();
  // Run keys also start with `kitting-pick:` — exclude them so they are not treated as slots.
  if (trimmed.startsWith(KITTING_PICK_RUN_JOB_PREFIX)) return [];
  if (!trimmed.startsWith(KITTING_PICK_JOB_PREFIX)) return [];
  const rest = trimmed.slice(KITTING_PICK_JOB_PREFIX.length).trim();
  if (rest === "") return [];
  return rest
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}
